'use client';

import { Button, Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useInterval } from '@mantine/hooks';
import { useNotifications } from '@repo/hooks';
import { IconCheck, IconPhone, IconRefresh } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { PhoneNumberInput } from '@website/components/phone-number-input';
import { setToken } from '@website/server/actions/auth';
import { api, handleTreatyResponse } from '@website/utils/api';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

export function LoginForm() {
  const t = useTranslations();
  const n = useNotifications();
  const [seconds, setSeconds] = useState(60);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const interval = useInterval(() => {
    setSeconds((s) => {
      if (s === 0) {
        interval.stop();
      }

      return s - 1;
    });
  }, 1000);

  const form = useForm({
    initialValues: {
      username: '',
      code: '',
    },
    transformValues: (values) => ({
      ...values,
      username: values.username.replaceAll(' ', ''),
    }),
  });

  const loginMut = useMutation({
    onSuccess: () => {
      setSeconds(60);
      interval.start();

      setIsCodeSent(true);

      n.success(t('login.codeSent'));
    },
    mutationFn: async () => {
      const res = await api.accounts.login.post({
        username: form.getTransformedValues().username,
      });

      return handleTreatyResponse(res);
    },
  });

  const verifyMut = useMutation({
    mutationFn: async () => {
      const { username, code } = form.getTransformedValues();

      const res = await api.accounts.verify.post({ username, code });

      return handleTreatyResponse(res);
    },

    onSuccess: async ({ token }) => {
      await setToken(token);

      n.success(t('login.succeed'));
      window.location.href = '/';
    },
  });

  const handleSubmit = form.onSubmit(async () => {
    if (isCodeSent) {
      await verifyMut.mutateAsync();
    } else {
      await loginMut.mutateAsync();
    }
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <PhoneNumberInput
          dir="ltr"
          label={t('login.phoneNumber')}
          placeholder="+964 700 000 0000"
          {...form.getInputProps('username')}
        />

        {!isCodeSent ? (
          <Button
            variant="light"
            leftSection={<IconPhone />}
            loading={loginMut.isPending}
            onClick={() => loginMut.mutate()}
          >
            {t('login.sendCode')}
          </Button>
        ) : (
          <>
            <TextInput
              label={t('login.code')}
              inputMode="numeric"
              placeholder="******"
              style={{ flexGrow: 1 }}
              {...form.getInputProps('code')}
            />

            <Group justify="end">
              <Button
                size="sm"
                miw={100}
                variant="subtle"
                disabled={seconds > 0}
                loading={loginMut.isPending}
                onClick={() => loginMut.mutate()}
                leftSection={<IconRefresh size={16} />}
              >
                {seconds > 0 ? seconds : t('login.resendCode')}
              </Button>
            </Group>

            <Button
              type="submit"
              leftSection={<IconCheck />}
              loading={verifyMut.isPending}
            >
              {t('login.login')}
            </Button>
          </>
        )}
      </Stack>
    </form>
  );
}
